<template>
  <div class="flex flex-col">
    <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
      <p class="text-sm text-gray-600 flex-1 min-w-0">
        View asynchronous helpdesk ticket submission logs and retrigger failed or pending submissions to the integration service.
      </p>
      <button type="button" class="btn shrink-0" :disabled="loading" @click="loadLogs">
        <i class="fa-solid fa-rotate-right" :class="{ 'fa-spin': loading }"></i>
        <span>Refresh</span>
      </button>
    </div>

    <div v-if="loading && logs.length === 0" class="w-full bg-gray-200 rounded-full h-1 overflow-hidden mb-4">
      <div class="bg-primary h-1 rounded-full animate-pulse" style="width: 100%"></div>
    </div>

    <div class="bg-white rounded border border-gray-200 overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full text-xs">
          <thead class="bg-gray-50 sticky top-0">
            <tr>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200 w-16">#</th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Subject</th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">User</th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Status</th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200 w-20">HTTP</th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Error</th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Created</th>
              <th class="text-center py-3 px-4 font-semibold text-gray-700 border-b border-gray-200 w-44">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(log, idx) in logs"
              :key="log.id"
              class="hover:bg-gray-50 transition"
            >
              <td class="py-3 px-4 border-b border-gray-200">{{ idx + 1 }}</td>
              <td class="py-3 px-4 border-b border-gray-200 font-medium max-w-[10rem] truncate" :title="log.subject || undefined">
                {{ log.subject || "—" }}
              </td>
              <td class="py-3 px-4 border-b border-gray-200 max-w-[12rem]">
                <div class="font-medium truncate" :title="formatUserName(log)">{{ formatUserName(log) }}</div>
                <div class="text-gray-500 truncate" :title="log.user?.email || undefined">{{ log.user?.email || "—" }}</div>
              </td>
              <td class="py-3 px-4 border-b border-gray-200">
                <span class="px-2 py-0.5 rounded-full text-xs font-semibold" :class="statusBadgeClass(log.status)">
                  {{ formatStatus(log.status) }}
                </span>
              </td>
              <td class="py-3 px-4 border-b border-gray-200 text-center">
                <span
                  v-if="log.responseStatus != null"
                  class="px-2 py-0.5 rounded-full text-xs font-semibold"
                  :class="httpStatusBadgeClass(log.responseStatus)"
                >
                  {{ log.responseStatus }}
                </span>
                <span v-else>—</span>
              </td>
              <td class="py-3 px-4 border-b border-gray-200 max-w-[14rem] truncate text-red-700" :title="log.errorMessage || undefined">
                {{ log.errorMessage || "—" }}
              </td>
              <td class="py-3 px-4 border-b border-gray-200 whitespace-nowrap">{{ formatLogTime(log.createdAt) }}</td>
              <td class="py-3 px-4 border-b border-gray-200">
                <div class="flex items-center justify-center gap-2 flex-wrap">
                  <button type="button" class="btn-sm-primary" @click="viewDetail(log)">
                    <i class="fa-solid fa-eye"></i>
                    <span>View</span>
                  </button>
                  <button
                    type="button"
                    class="btn-sm-danger"
                    :disabled="retriggeringId === log.id"
                    @click="confirmRetrigger(log)"
                  >
                    <i v-if="retriggeringId === log.id" class="fa fa-spinner fa-spin"></i>
                    <i v-else class="fa-solid fa-rotate-right"></i>
                    <span>Retrigger</span>
                  </button>
                </div>
              </td>
            </tr>
            <tr v-if="logs.length === 0 && !loading">
              <td colspan="8" class="py-8 px-4 text-center text-gray-500">
                No ticket submission logs found.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <AppModal v-model="showDetail" title="Ticket Log Details" size="lg" hide-footer>
      <div v-if="selectedLog" class="log-detail">
        <p v-for="entry in detailEntries" :key="entry.key" class="detail-row">
          <span class="detail-label">{{ entry.label }}:</span>
          <span class="detail-value-wrap">{{ entry.value }}</span>
        </p>
      </div>
    </AppModal>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import AppModal from "@/components/ui/AppModal.vue";
import { useConfirmDialog, useToast, useUtilities } from "@/composables";
import {
  buildRetriggerToast,
  extractErrorMessage,
  fetchHelpDeskTicketLogs,
  retriggerHelpDeskTicketLog,
  type HelpDeskTicketLog,
} from "./helpDeskApi";

const { successToast, errorToast, warningToast } = useToast();
const { showConfirm } = useConfirmDialog();
const { dateFormat } = useUtilities();

const logs = ref<HelpDeskTicketLog[]>([]);
const loading = ref(false);
const retriggeringId = ref<string | null>(null);
const showDetail = ref(false);
const selectedLog = ref<HelpDeskTicketLog | null>(null);

const detailEntries = computed(() => {
  const log = selectedLog.value;
  if (!log) return [];

  return [
    { key: "id", label: "Log ID", value: log.id },
    { key: "subject", label: "Subject", value: log.subject || "—" },
    { key: "description", label: "Description", value: log.description || "—" },
    { key: "status", label: "Status", value: formatStatus(log.status) },
    { key: "userName", label: "User", value: formatUserName(log) },
    { key: "email", label: "Email", value: log.user?.email || "—" },
    { key: "accountType", label: "Account type", value: log.user?.accountType || "—" },
    { key: "phoneNumber", label: "Phone", value: log.user?.phoneNumber || "—" },
    { key: "httpMethod", label: "HTTP method", value: log.httpMethod || "—" },
    { key: "outgoingUrl", label: "Outgoing URL", value: log.outgoingUrl || "—" },
    {
      key: "responseStatus",
      label: "Response status",
      value: log.responseStatus != null ? String(log.responseStatus) : "—",
    },
    { key: "errorMessage", label: "Error", value: log.errorMessage || "—" },
    { key: "createdAt", label: "Created at", value: formatLogTime(log.createdAt) },
    { key: "updatedAt", label: "Updated at", value: formatLogTime(log.updatedAt) },
  ];
});

const formatUserName = (log: HelpDeskTicketLog) => {
  if (log.user?.fullName) return log.user.fullName;
  if (log.user?.email) return log.user.email;
  return "—";
};

const formatStatus = (status: string | null) => {
  if (!status) return "—";
  return status.replace(/_/g, " ");
};

const statusBadgeClass = (status: string | null) => {
  const normalized = (status || "").toLowerCase();
  if (normalized.includes("success") || normalized.includes("complete") || normalized.includes("submitted")) {
    return "bg-green-100 text-green-800";
  }
  if (normalized.includes("fail") || normalized.includes("error")) {
    return "bg-red-100 text-red-800";
  }
  if (normalized.includes("pending") || normalized.includes("queue") || normalized.includes("processing")) {
    return "bg-yellow-100 text-yellow-800";
  }
  return "bg-gray-100 text-gray-600";
};

const httpStatusBadgeClass = (status: number) => {
  if (status >= 200 && status < 300) return "bg-green-100 text-green-800";
  if (status >= 400) return "bg-red-100 text-red-800";
  return "bg-yellow-100 text-yellow-800";
};

const formatLogTime = (value: string | null) => {
  if (!value) return "—";
  return dateFormat(value) || value;
};

const viewDetail = (log: HelpDeskTicketLog) => {
  selectedLog.value = log;
  showDetail.value = true;
};

const loadLogs = async () => {
  loading.value = true;
  try {
    logs.value = await fetchHelpDeskTicketLogs();
  } catch (error: unknown) {
    logs.value = [];
    errorToast("Error", extractErrorMessage(error, "Could not load ticket logs"));
  } finally {
    loading.value = false;
  }
};

const confirmRetrigger = async (log: HelpDeskTicketLog) => {
  const subject = log.subject || log.id;
  const confirmed = await showConfirm(
    `Retrigger helpdesk submission for "${subject}"?`,
    {
      title: "Retrigger Submission",
      variant: "danger",
      okTitle: "Retrigger",
      cancelTitle: "Cancel",
    }
  );
  if (!confirmed) return;

  retriggeringId.value = log.id;
  try {
    const result = await retriggerHelpDeskTicketLog(log.id);
    const toast = buildRetriggerToast(result);
    if (toast.type === "warning") {
      warningToast(toast.title, toast.text);
    } else {
      successToast(toast.title, toast.text);
    }
    await loadLogs();
  } catch (error: unknown) {
    errorToast("Error", extractErrorMessage(error, "Could not retrigger ticket submission"));
  } finally {
    retriggeringId.value = null;
  }
};

onMounted(() => {
  loadLogs();
});
</script>

<style scoped>
.log-detail .detail-row {
  margin-bottom: 0.5rem;
  font-size: 14px;
}

.log-detail .detail-label {
  font-weight: 600;
  margin-right: 0.5rem;
  min-width: 120px;
  display: inline-block;
  vertical-align: top;
}

.log-detail .detail-value-wrap {
  word-break: break-all;
  white-space: pre-wrap;
}
</style>
